import React, { useState, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../../utils/poFormatters.jsx';
import { sessionsAPI } from '../../services/api';
import { toast } from 'sonner';

const formatMonthLabel = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mo, 10) - 1]} '${y.slice(2)}`;
};

export const MonthlyConsumptionChart = ({ data, onRefresh, title = 'Month-wise Budget' }) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [view, setView] = useState('consumed'); // 'consumed' (completed_month) | 'invoiced' (invoiced_month)
  const savingRef = useRef(false);

  const sessions = data?.sessions || [];

  // Recompute the monthly bars client-side based on the selected grouping field
  const monthly = useMemo(() => {
    const key = view === 'invoiced' ? 'invoiced_month' : 'completed_month';
    const totals = {};
    sessions.forEach(s => {
      const mk = s[key];
      if (!mk) return;
      totals[mk] = (totals[mk] || 0) + (s.amount || 0);
    });
    return Object.entries(totals)
      .map(([month, amount]) => ({ month, amount: Math.round(amount * 100) / 100 }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [sessions, view]);

  const saveInvoicedMonth = async (session) => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      await sessionsAPI.update(session.session_id, { invoiced_month: editValue || null });
      toast.success('Invoiced month updated');
      setEditingId(null);
      onRefresh && onRefresh();
    } catch {
      toast.error('Failed to update invoiced month');
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <Card data-testid="monthly-consumption-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="font-heading text-base">
            {title} — {view === 'invoiced' ? 'Invoiced' : 'Consumed'}
          </CardTitle>
          <CardDescription className="text-xs">
            {view === 'invoiced' ? 'Budget invoiced per month' : 'Budget used per month, based on completed sessions'}
          </CardDescription>
        </div>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="monthly-consumption-view-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="consumed">Consumed</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {monthly.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthly} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} />
              <RechartsTooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonthLabel} />
              <Bar dataKey="amount" fill="#C8E600" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-muted-foreground py-8 text-sm">No completed sessions with cost yet</p>
        )}

        {sessions.length > 0 && (
          <div className="mt-5 max-h-72 overflow-y-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Completed Month</TableHead>
                  <TableHead>Invoiced Month</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map(s => (
                  <TableRow key={s.session_id} data-testid={`consumption-row-${s.session_id}`}>
                    <TableCell className="text-sm">{s.session_title}</TableCell>
                    <TableCell className="text-sm">{s.project_title}</TableCell>
                    <TableCell className="text-sm">{s.po_number || '—'}</TableCell>
                    <TableCell className="text-sm">{formatCurrency(s.amount)}</TableCell>
                    <TableCell className="text-sm">{formatMonthLabel(s.completed_month)}</TableCell>
                    <TableCell>
                      {editingId === s.session_id ? (
                        <Input
                          type="month"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => saveInvoicedMonth(s)}
                          autoFocus
                          className="h-8 w-36"
                          data-testid={`invoiced-month-input-${s.session_id}`}
                        />
                      ) : (
                        <button
                          type="button"
                          className="text-sm text-blue-600 hover:underline"
                          onClick={() => { setEditingId(s.session_id); setEditValue(s.invoiced_month || ''); }}
                          data-testid={`invoiced-month-edit-${s.session_id}`}
                        >
                          {formatMonthLabel(s.invoiced_month)}
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MonthlyConsumptionChart;
